import { Printer, Download, ArrowLeft } from "lucide-react";

/**
 * Printable strategic study for the Prolancer Calculator.
 * Visit: /strategic-study
 * Press "طباعة / حفظ PDF" to export a clean A4 PDF.
 */
export default function StrategicStudy() {
  return (
    <div dir="rtl" className="strat-root min-h-screen bg-white text-[#182138]">
      {/* Floating toolbar — hidden in print */}
      <div className="strat-toolbar no-print">
        <button
          onClick={() => window.history.back()}
          className="strat-btn strat-btn-ghost"
          data-testid="strat-back"
        >
          <ArrowLeft className="h-4 w-4" />
          رجوع
        </button>
        <button
          onClick={() => window.print()}
          className="strat-btn strat-btn-primary"
          data-testid="strat-print"
        >
          <Printer className="h-4 w-4" />
          طباعة / حفظ PDF
        </button>
      </div>

      <article className="strat-doc">
        {/* Cover */}
        <header className="strat-cover">
          <div className="strat-brand">
            <img src="/prolancer-bird.png" alt="Prolancer" />
            <div>
              <div className="strat-brand-name">Prolancer — برولانسر</div>
              <div className="strat-brand-sub">دراسة استراتيجية — الحاسبة</div>
            </div>
          </div>
          <h1>
            دراسة استراتيجية وجدوى اقتصادية
            <br />
            لحاسبة أسعار العمل الحر — برولانسر
          </h1>
          <p className="strat-lead">
            أداة ذكاء اصطناعي مساعِدة لمنصة <b>برولانسر</b> السعودية للمستقلين الموثّقين،
            تدعم تسعير الخدمات باللغتين العربية والإنجليزية، وتُغطّي ٨ عملات لتخدم
            جميع الأسواق الناطقة بالعربية والأسواق المستهدَفة من قِبل المستقلين السعوديين.
          </p>
          <div className="strat-meta">
            <span>تاريخ الإصدار: فبراير ٢٠٢٦</span>
            <span>الإصدار: v1.0</span>
            <span>النطاق: prolancer.app</span>
          </div>
        </header>

        {/* 1. Executive Summary */}
        <section className="strat-sec">
          <h2>١. الملخّص التنفيذي</h2>
          <p>
            حاسبة برولانسر منتج رقمي خفيف لكنه عالي الأثر: يحلّ مشكلة <b>"كم أسعّر خدمتي؟"</b>
            الأعلى تردّدًا بين المستقلين الجدد في السوق السعودي. تجمع الحاسبة بين قاعدة أسعار
            سعودية حقيقية محدَّثة يوميًا، ومحرّك ذكاء اصطناعي (Claude Haiku 4.5) يقترح سعرًا
            متوازنًا بين الربحية والفوز بالطلب. يُقدَّم المنتج مجانًا للمستخدم العادي، مع نموذج
            اشتراك فريميوم للمحترفين (٩٩ ر.س شهريًا / ٤٩٩ ر.س سنويًا).
          </p>
          <ul className="strat-list">
            <li><b>الموقع التنافسي:</b> أوّل حاسبة سعودية مُحسَّنة بـ AI وبدعم ٨ عملات.</li>
            <li><b>الفرصة:</b> ٥٠٠٬٠٠٠+ ممارس عمل حر مرخَّص في المملكة بنهاية ٢٠٢٥ (مُنشآت)،
              نموّ سنوي ٢٥٪ وفق رؤية ٢٠٣٠.</li>
            <li><b>الإيراد المستهدَف السنة الأولى:</b> ٢٦٥٬٠٠٠ ر.س (سيناريو معتدل).</li>
            <li><b>التكلفة التشغيلية السنوية:</b> أقل من ١٢٬٠٠٠ ر.س (هامش ربح ٩٥٪+).</li>
            <li><b>ميزة دفاعية:</b> ارتباط مباشر ببرولانسر + أصول SEO (٨ صفحات هبوط عربية).</li>
          </ul>
        </section>

        {/* 2. Market Analysis */}
        <section className="strat-sec">
          <h2>٢. تحليل السوق</h2>
          <h3>السوق السعودي</h3>
          <ul className="strat-list">
            <li>الاقتصاد الحر في المملكة ينمو بوتيرة <b>٢٥٪ سنويًا</b> (تقارير مُنشآت ٢٠٢٥).</li>
            <li>عدد ممارسي العمل الحر الموثَّقين عبر وثيقة العمل الحر تجاوز <b>٥٠٠٬٠٠٠</b> نهاية ٢٠٢٥.</li>
            <li>متوسط دخل المستقل السعودي: ٤٬٥٠٠ – ١٢٬٠٠٠ ر.س شهريًا، ويعتمد ٨٠٪ منهم على التسعير التقديري بلا مرجع.</li>
            <li>أعلى ٥ خدمات طلبًا: التسويق الرقمي، تطوير المواقع، تصميم الهوية، كتابة المحتوى، إدارة السوشال.</li>
          </ul>

          <h3>السوق الإقليمي والعربي (العملات المدعومة)</h3>
          <table className="strat-table">
            <thead>
              <tr>
                <th>السوق</th>
                <th>العملة</th>
                <th>حجم المستقلين (تقديريًا ٢٠٢٥)</th>
                <th>الفرصة</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>السعودية</td><td>SAR</td><td>500,000+</td><td>السوق الرئيسي</td></tr>
              <tr><td>الإمارات</td><td>AED</td><td>130,000</td><td>هامش عالٍ · فُرَص خليجية</td></tr>
              <tr><td>مصر</td><td>EGP</td><td>1,500,000</td><td>حجم ضخم · تسعير بالدولار</td></tr>
              <tr><td>الكويت</td><td>KWD</td><td>45,000</td><td>قدرة شرائية عالية</td></tr>
              <tr><td>قطر</td><td>QAR</td><td>35,000</td><td>مشاريع حكومية</td></tr>
              <tr><td>البحرين</td><td>BHD</td><td>18,000</td><td>سوق متنامٍ</td></tr>
              <tr><td>USD/EUR</td><td>عالمي</td><td>—</td><td>تسعير العمل عن بُعد</td></tr>
            </tbody>
          </table>
          <p className="strat-note">
            المصادر: تقارير مُنشآت ٢٠٢٥، MAGNiTT، Statista Gig Economy MENA 2025 — أرقام تقديرية.
          </p>
        </section>

        {/* 3. SWOT */}
        <section className="strat-sec strat-page-break">
          <h2>٣. تحليل SWOT</h2>
          <div className="strat-swot">
            <div className="strat-swot-cell strat-s">
              <h4>نقاط القوة (Strengths)</h4>
              <ul>
                <li>أول حاسبة سعودية مُحسَّنة بـ AI وبدعم ٨ عملات.</li>
                <li>قاعدة أسعار سوقية حقيقية محدَّثة.</li>
                <li>تكامل مع برولانسر — بوصلة تحويل مباشرة لمنصة سعودية موثوقة.</li>
                <li>واجهة ثنائية اللغة (عربي RTL / إنجليزي LTR).</li>
                <li>نموذج فريميوم مربح + كاش ذكي يقلّل تكاليف LLM بنسبة ٤٠-٧٠٪.</li>
                <li>SEO قوي (٨ صفحات هبوط عربية + Schema.org + Open Graph).</li>
                <li>قابلية التضمين (iframe) على مواقع خارجية مع حماية براند متعدّدة الطبقات.</li>
              </ul>
            </div>
            <div className="strat-swot-cell strat-w">
              <h4>نقاط الضعف (Weaknesses)</h4>
              <ul>
                <li>اعتماد كبير على منصة واحدة (برولانسر) للترويج.</li>
                <li>عدم وجود تطبيق جوال أصلي حتى الآن.</li>
                <li>مصادقة المستخدمين تعتمد حاليًا على البريد الإلكتروني فقط.</li>
                <li>الذكاء الاصطناعي يعتمد على مزوّد واحد (Anthropic Claude).</li>
                <li>لا توجد لوحة تحكم شخصية للمستقل تحفظ عروضه السابقة بشكل كامل.</li>
              </ul>
            </div>
            <div className="strat-swot-cell strat-o">
              <h4>الفُرص (Opportunities)</h4>
              <ul>
                <li>التوسّع إقليميًا (الخليج ومصر) باستخدام العملات المدعومة.</li>
                <li>شراكة رسمية مع برولانسر لتثبيت الحاسبة داخل حسابات المستقلين.</li>
                <li>إضافة Chrome Extension لتسعير المشاريع من داخل المنصّات.</li>
                <li>API مدفوع للمنصّات المنافسة (White-Label).</li>
                <li>عقد شراكات مع المؤثرين السعوديين في مجال العمل الحر.</li>
                <li>بيع كورس تدريبي "تسعير احترافي" مرتبط بالحاسبة (٢٩٩ ر.س).</li>
                <li>إضافة ضريبة القيمة المضافة (١٥٪) تلقائيًا لعرض الأسعار النهائية.</li>
              </ul>
            </div>
            <div className="strat-swot-cell strat-t">
              <h4>التهديدات (Threats)</h4>
              <ul>
                <li>اعتماد المنصات الكبرى (مستقل، خمسات) على حاسبات داخلية مجانية.</li>
                <li>تراجع تكلفة نماذج AI → ظهور منافسين سريعين.</li>
                <li>تقلّب أسعار الصرف قد يُشوّش دقّة التحويل بين العملات.</li>
                <li>تغيّر سياسات هيئة المحتوى أو مُنشآت على التراخيص.</li>
                <li>فقدان ثقة المستخدم في حالة ارتفاع نسبة الخطأ في الاقتراحات.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 4. Personas */}
        <section className="strat-sec">
          <h2>٤. شرائح المستخدمين المستهدَفة</h2>
          <table className="strat-table">
            <thead>
              <tr>
                <th>الشريحة</th>
                <th>المشكلة الأساسية</th>
                <th>القيمة من الحاسبة</th>
                <th>نسبة متوقَّعة</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>أ. المستقل الموثَّق على برولانسر</b></td>
                <td>الحاجة لتسعير عادل يفوز بأول طلبات</td>
                <td>اقتراح AI + استراتيجية رفع السعر لاحقًا</td>
                <td>40%</td>
              </tr>
              <tr>
                <td><b>ب. المستقل السعودي الجديد</b></td>
                <td>عدم ثقة في تسعير مهاراته</td>
                <td>تعليم + ثقة + قرب من السوق</td>
                <td>25%</td>
              </tr>
              <tr>
                <td><b>ج. المستقل الخليجي</b></td>
                <td>مرجع تسعير بعملته (AED, KWD, QAR, BHD)</td>
                <td>تحويل تلقائي + نصائح موحَّدة</td>
                <td>15%</td>
              </tr>
              <tr>
                <td><b>د. المستقل المصري</b></td>
                <td>تسعير بالدولار للعملاء الخليجيين</td>
                <td>عملة EGP + USD + توصية سوق خليجي</td>
                <td>12%</td>
              </tr>
              <tr>
                <td><b>هـ. وكالات ومستقلين دوليين</b></td>
                <td>فهم ديناميكية السوق السعودي</td>
                <td>مرجع موثوق + واجهة إنجليزية</td>
                <td>8%</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 5. Business Model */}
        <section className="strat-sec strat-page-break">
          <h2>٥. نموذج الأعمال ومصادر الدخل</h2>
          <table className="strat-table">
            <thead>
              <tr>
                <th>مصدر الدخل</th>
                <th>السعر</th>
                <th>الوصف</th>
                <th>الأولوية</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>اشتراك شهري غير محدود</td>
                <td>99 ر.س / شهر</td>
                <td>اقتراحات AI لامحدودة + تصدير PDF غير محدود</td>
                <td>P0 — قائم</td>
              </tr>
              <tr>
                <td>اشتراك سنوي غير محدود</td>
                <td>499 ر.س / سنة</td>
                <td>وفورات ٥٨٪ على السعر الشهري</td>
                <td>P0 — قائم</td>
              </tr>
              <tr>
                <td>باقات رصيد (Credits)</td>
                <td>5 – 49 ر.س</td>
                <td>٥ إلى ١٠٠ اقتراح AI دفعة واحدة</td>
                <td>P0 — قائم</td>
              </tr>
              <tr>
                <td>عمولة الإحالة لبرولانسر</td>
                <td>~20 ر.س / حساب موثَّق</td>
                <td>تحويل الزوار إلى عملاء برولانسر</td>
                <td>P1 — مقترَح</td>
              </tr>
              <tr>
                <td>ترخيص API / White-Label</td>
                <td>299 – 1,999 ر.س / شهر</td>
                <td>تضمين الحاسبة في مواقع ومنصّات الوكالات</td>
                <td>P1 — مقترَح</td>
              </tr>
              <tr>
                <td>كورس تسعير احترافي</td>
                <td>299 ر.س / كورس</td>
                <td>فيديوهات + قوالب + ترقية حساب برولانسر</td>
                <td>P2 — مستقبلي</td>
              </tr>
              <tr>
                <td>تحليلات متقدّمة للمستقل</td>
                <td>29 ر.س / شهر</td>
                <td>مقارنات شخصية + تنبؤات دخل سنوي</td>
                <td>P2 — مستقبلي</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 6. 12-Month Forecast */}
        <section className="strat-sec">
          <h2>٦. دراسة الجدوى — خطة ١٢ شهرًا</h2>
          <h3>٦.١ توقّعات الزوار (سيناريو معتدل)</h3>
          <table className="strat-table strat-table-sm">
            <thead>
              <tr>
                <th>الشهر</th>
                <th>M1</th><th>M2</th><th>M3</th><th>M4</th>
                <th>M5</th><th>M6</th><th>M7</th><th>M8</th>
                <th>M9</th><th>M10</th><th>M11</th><th>M12</th>
                <th>الإجمالي</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>زوار</td>
                <td>1k</td><td>2k</td><td>3.5k</td><td>5.5k</td>
                <td>8k</td><td>11k</td><td>14.5k</td><td>18k</td>
                <td>22k</td><td>26k</td><td>30k</td><td>35k</td>
                <td><b>176.5k</b></td>
              </tr>
            </tbody>
          </table>

          <h3>٦.٢ قمع التحويل (Conversion Funnel)</h3>
          <table className="strat-table">
            <thead>
              <tr>
                <th>المرحلة</th>
                <th>النسبة</th>
                <th>العدد السنوي</th>
                <th>متوسط القيمة</th>
                <th>الإيراد (ر.س)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>زوار الصفحة الرئيسية</td>
                <td>100%</td>
                <td>176,500</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>مُستخدِم للحاسبة</td>
                <td>45%</td>
                <td>79,425</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>مُستخدِم اقتراح AI</td>
                <td>15%</td>
                <td>26,475</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>شراء باقة رصيد</td>
                <td>2%</td>
                <td>3,530</td>
                <td>15 ر.س</td>
                <td><b>52,950</b></td>
              </tr>
              <tr>
                <td>اشتراك شهري</td>
                <td>0.6%</td>
                <td>265 اشتراك</td>
                <td>99 ر.س × 3 أشهر</td>
                <td><b>78,705</b></td>
              </tr>
              <tr>
                <td>اشتراك سنوي</td>
                <td>0.3%</td>
                <td>265 اشتراك</td>
                <td>499 ر.س</td>
                <td><b>132,235</b></td>
              </tr>
              <tr>
                <td>عمولة إحالة لبرولانسر</td>
                <td>1%</td>
                <td>1,765 حساب</td>
                <td>20 ر.س</td>
                <td><b>35,300</b></td>
              </tr>
              <tr className="strat-total-row">
                <td colSpan={4}><b>إجمالي الإيرادات المتوقَّعة (سنة ١)</b></td>
                <td><b>299,190 ر.س</b></td>
              </tr>
            </tbody>
          </table>

          <h3>٦.٣ التكاليف التشغيلية السنوية</h3>
          <table className="strat-table">
            <thead>
              <tr>
                <th>البند</th>
                <th>التكلفة الشهرية</th>
                <th>التكلفة السنوية</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Emergent LLM Key (Claude Haiku مع كاش ذكي)</td>
                <td>~110 ر.س</td>
                <td>~1,320 ر.س</td>
              </tr>
              <tr>
                <td>استضافة Emergent (deployed)</td>
                <td>مجّاني من الرصيد المسبق</td>
                <td>0 ر.س</td>
              </tr>
              <tr>
                <td>دومين prolancer.app</td>
                <td>—</td>
                <td>~75 ر.س</td>
              </tr>
              <tr>
                <td>Tap Payments (رسوم 2.75% + 1 ر.س / عملية)</td>
                <td>~250 ر.س</td>
                <td>~3,000 ر.س</td>
              </tr>
              <tr>
                <td>تسويق محتوى + SEO (كتابة + سوشال)</td>
                <td>500 ر.س</td>
                <td>6,000 ر.س</td>
              </tr>
              <tr>
                <td>قانوني ومحاسبي (شهادة + ضريبة)</td>
                <td>—</td>
                <td>2,000 ر.س</td>
              </tr>
              <tr className="strat-total-row">
                <td><b>إجمالي التكاليف</b></td>
                <td></td>
                <td><b>~12,395 ر.س</b></td>
              </tr>
            </tbody>
          </table>

          <h3>٦.٤ صافي الربح — ثلاثة سيناريوهات</h3>
          <table className="strat-table">
            <thead>
              <tr>
                <th>السيناريو</th>
                <th>إيرادات</th>
                <th>تكاليف</th>
                <th>صافي الربح</th>
                <th>هامش</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>متحفّظ (نموّ بطيء 50٪ من المعتدل)</td>
                <td>149,595 ر.س</td>
                <td>8,500 ر.س</td>
                <td><b>141,095 ر.س</b></td>
                <td>94%</td>
              </tr>
              <tr className="strat-highlight">
                <td>معتدل (الأرجح)</td>
                <td>299,190 ر.س</td>
                <td>12,395 ر.س</td>
                <td><b>286,795 ر.س</b></td>
                <td>95.8%</td>
              </tr>
              <tr>
                <td>متفائل (شراكة برولانسر + فيروسية)</td>
                <td>550,000 ر.س</td>
                <td>22,000 ر.س</td>
                <td><b>528,000 ر.س</b></td>
                <td>96%</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 7. Strategic Recommendations */}
        <section className="strat-sec strat-page-break">
          <h2>٧. التوصيات الاستراتيجية</h2>

          <h3>٧.١ قصيرة المدى (٠ – ٣ أشهر)</h3>
          <ol className="strat-list strat-list-ol">
            <li><b>شراكة رسمية مع برولانسر:</b> تثبيت الحاسبة كأداة رسمية داخل لوحة
              تحكم المستقل الموثَّق + صفحة تعريفية مشتركة.</li>
            <li><b>تفعيل عمولة الإحالة:</b> ربط كل زائر برابط إحالة فريد يُسجَّل عند
              التسجيل في برولانسر (20 ر.س / حساب موثَّق).</li>
            <li><b>حملة سوشال ميديا مُركَّزة:</b> ٣ منشورات / أسبوع على X وLinkedIn و
              Threads بالأرقام والتوصيات الحقيقية من الحاسبة.</li>
            <li><b>مصادقة JWT كاملة:</b> حسابات شخصية تحفظ العروض السابقة والسجلّ
              المالي عبر الأجهزة (مذكور ضمن الـ Backlog).</li>
            <li><b>إضافة ضريبة القيمة المضافة (١٥٪) اختيارية:</b> لعرض السعر النهائي
              للعميل بشفافية — مطلب شائع من المستقلين السعوديين.</li>
          </ol>

          <h3>٧.٢ متوسطة المدى (٣ – ٦ أشهر)</h3>
          <ol className="strat-list strat-list-ol">
            <li><b>Chrome Extension رسمية:</b> زرّ تسعير فوري داخل داشبورد برولانسر و
              منصّات مماثلة؛ يسحب تفاصيل العرض ويُعيد السعر الموصى به في ٣ ثوانٍ.</li>
            <li><b>تطبيق جوال iOS + Android:</b> React Native يحتفظ بنفس الـ API،
              إشعارات تذكير لتجديد الاشتراك، مزامنة PDF مع iCloud/Drive.</li>
            <li><b>White-Label API:</b> ترخيص الحاسبة لوكالات ومنصّات أخرى (٢٩٩-١٬٩٩٩
              ر.س / شهر) مع لوحة إحصائيات مخصّصة لكل عميل.</li>
            <li><b>شراكات تسويقية:</b> ٥ مؤثرين سعوديين في مجال العمل الحر، مقابل
              نسبة إحالة ٢٠٪ من إيرادات الاشتراكات التي يجلبونها.</li>
            <li><b>تحسين AI Prompt Caching:</b> رفع نسبة إصابة الكاش من ٥٠٪ إلى ٨٠٪
              عبر خفض دقّة فروق السعر إلى ١٠ ر.س، لتوفير إضافي ~٦٠٪ من تكاليف LLM.</li>
          </ol>

          <h3>٧.٣ طويلة المدى (٦ – ١٢ شهر)</h3>
          <ol className="strat-list strat-list-ol">
            <li><b>كورس "تسعير احترافي":</b> ٨ دروس فيديو + ١٠ قوالب + شهادة حضور،
              ٢٩٩ ر.س، مع تخفيض ٥٠٪ للمشتركين السنويين.</li>
            <li><b>حاسبة الفواتير / Invoicing Module:</b> توليد فواتير قانونية ممتثلة
              للزكاة والدخل تلقائيًا من كل عرض سعر — مدفوع بـ ١٩ ر.س/شهر.</li>
            <li><b>التوسع في منصة الخليج:</b> صفحات هبوط إقليمية مخصّصة
              (الإمارات، الكويت، قطر، البحرين) مع عملة ولغة محلية.</li>
            <li><b>نسخة B2B للوكالات:</b> حاسبة متقدّمة متعدّدة المشاريع والفريق،
              تسعير دفعة واحدة لعدة خدمات، نظام عرض مميّز.</li>
            <li><b>مؤشر برولانسر للعمل الحر (Index):</b> نشرة ربع-سنوية مجّانية تحدّد
              متوسط أسعار كل قطاع — تُصبح مرجعًا للصحافة والمحتوى.</li>
          </ol>
        </section>

        {/* 8. KPIs */}
        <section className="strat-sec">
          <h2>٨. مؤشّرات الأداء الرئيسية (KPIs)</h2>
          <table className="strat-table">
            <thead>
              <tr>
                <th>المؤشر</th>
                <th>الهدف (شهر ٣)</th>
                <th>الهدف (شهر ٦)</th>
                <th>الهدف (شهر ١٢)</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>زوار شهريًا</td><td>3,500</td><td>11,000</td><td>35,000</td></tr>
              <tr><td>جلسات حاسبة شهريًا</td><td>1,575</td><td>4,950</td><td>15,750</td></tr>
              <tr><td>طلبات AI شهريًا</td><td>525</td><td>1,650</td><td>5,250</td></tr>
              <tr><td>نسبة إصابة الكاش</td><td>40%</td><td>65%</td><td>80%</td></tr>
              <tr><td>مشتركون نشطون</td><td>35</td><td>120</td><td>400</td></tr>
              <tr><td>إيراد شهري (ر.س)</td><td>5,000</td><td>18,000</td><td>60,000</td></tr>
              <tr><td>متوسط دخل / مشترك</td><td>14 ر.س</td><td>15 ر.س</td><td>15 ر.س</td></tr>
              <tr><td>معدّل الاحتفاظ الشهري</td><td>65%</td><td>72%</td><td>78%</td></tr>
            </tbody>
          </table>
        </section>

        {/* 9. Risks */}
        <section className="strat-sec">
          <h2>٩. المخاطر وخطط التخفيف</h2>
          <table className="strat-table">
            <thead>
              <tr>
                <th>الخطر</th>
                <th>الاحتمال</th>
                <th>الأثر</th>
                <th>خطة التخفيف</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ارتفاع تكلفة LLM مستقبلًا</td>
                <td>متوسط</td>
                <td>عالي</td>
                <td>الكاش الذكي (٨٠٪ hit ratio) + سقف يومي ثابت من لوحة الأدمن.</td>
              </tr>
              <tr>
                <td>انقطاع خدمة Claude</td>
                <td>منخفض</td>
                <td>عالي</td>
                <td>وضع Fallback تلقائي للحاسبة التقليدية + دعم مزوّد ثانٍ (Gemini).</td>
              </tr>
              <tr>
                <td>ركود السوق السعودي</td>
                <td>منخفض</td>
                <td>متوسط</td>
                <td>تنويع جغرافي عبر دعم ٨ عملات والتوسع في مصر والخليج.</td>
              </tr>
              <tr>
                <td>انسحاب برولانسر من الشراكة</td>
                <td>منخفض</td>
                <td>عالي</td>
                <td>استقلال العلامة التجارية + علاقات مع ٣ منصّات بديلة.</td>
              </tr>
              <tr>
                <td>نسخ المنتج من قِبل منافس</td>
                <td>متوسط</td>
                <td>متوسط</td>
                <td>تسجيل العلامة التجارية + تطوير مستمر (أسبوعي) + أصول SEO عميقة.</td>
              </tr>
              <tr>
                <td>اختراق حقوق البراند في iframe</td>
                <td>منخفض</td>
                <td>منخفض</td>
                <td>ثلاث طبقات حماية (علامة مائية + Powered-by + Domain Lock) — مُنفَّذة.</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 10. Roadmap */}
        <section className="strat-sec strat-page-break">
          <h2>١٠. خارطة الطريق التنفيذية — ١٢ شهرًا</h2>
          <table className="strat-table">
            <thead>
              <tr>
                <th>الربع</th>
                <th>الإنجازات المستهدَفة</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>Q1 (شهر ١-٣)</b></td>
                <td>
                  شراكة برولانسر الرسمية · تفعيل الإحالة · حسابات JWT · ضريبة ١٥٪ · تحسين SEO + ٥ مقالات جديدة.
                </td>
              </tr>
              <tr>
                <td><b>Q2 (شهر ٤-٦)</b></td>
                <td>
                  Chrome Extension · أول عميل White-Label · ٥ شراكات مؤثرين · رفع نسبة الكاش إلى ٨٠٪.
                </td>
              </tr>
              <tr>
                <td><b>Q3 (شهر ٧-٩)</b></td>
                <td>
                  تطبيق جوال iOS + Android · حاسبة فواتير · صفحات هبوط إماراتية + كويتية · كورس تسعير محترف.
                </td>
              </tr>
              <tr>
                <td><b>Q4 (شهر ١٠-١٢)</b></td>
                <td>
                  نسخة B2B للوكالات · مؤشر برولانسر Q1 · تنويع مزوّد LLM (Gemini كـ fallback) · مراجعة الأسعار السنوية.
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 11. Conclusion */}
        <section className="strat-sec">
          <h2>١١. الخلاصة</h2>
          <p>
            حاسبة برولانسر منتج استراتيجي ذو تكلفة تشغيل منخفضة جدًا (أقل من ٥٪ من الإيرادات
            في السيناريو المعتدل) وهامش ربح يتجاوز <b>٩٥٪</b>. بوضعها كأداة مساعِدة رسمية
            لمنصة برولانسر، تحقّق الحاسبة ثلاثة أهداف استراتيجية في وقت واحد:
          </p>
          <ol className="strat-list strat-list-ol">
            <li><b>إيراد مباشر</b> من الاشتراكات والباقات (~٣٠٠ ألف ر.س / السنة الأولى).</li>
            <li><b>قناة تسويق فعّالة</b> لجذب المستقلين إلى منصة برولانسر.</li>
            <li><b>أصل رقمي قابل للتوسّع</b> إقليميًا وعالميًا بتكلفة هامشية صفرية تقريبًا.</li>
          </ol>
          <p>
            التوصية النهائية هي <b>الاستثمار في الشراكة الرسمية مع برولانسر</b>،
            وتفعيل عمولة الإحالة، وإطلاق Chrome Extension خلال الربع الثاني لتسريع النموّ
            إلى السيناريو المتفائل بإيرادات تتجاوز نصف مليون ريال سعودي سنويًا.
          </p>
          <div className="strat-signoff">
            <div>
              <div className="strat-signoff-label">إعداد:</div>
              <div className="strat-signoff-value">فريق استراتيجية برولانسر</div>
            </div>
            <div>
              <div className="strat-signoff-label">تحديث:</div>
              <div className="strat-signoff-value">فبراير ٢٠٢٦</div>
            </div>
            <div>
              <div className="strat-signoff-label">المرجع:</div>
              <div className="strat-signoff-value">prolancer.app/strategic-study</div>
            </div>
          </div>
        </section>

        <footer className="strat-footer">
          <div>© ٢٠٢٦ Prolancer / برولانسر — جميع الحقوق محفوظة</div>
          <div>وثيقة داخلية — للاستخدام الإداري والتسويقي · prolancer.app</div>
        </footer>
      </article>

      <style>{`
        .strat-root { font-family: 'Tajawal', system-ui, sans-serif; }
        .strat-toolbar { position: fixed; top: 16px; left: 16px; display: flex; gap: 8px; z-index: 100; }
        .strat-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 999px; font-weight: 800; font-size: 13px; border: 0; cursor: pointer; box-shadow: 0 10px 24px -8px rgba(15,23,42,0.3); transition: transform .15s; }
        .strat-btn:hover { transform: translateY(-1px); }
        .strat-btn-primary { background: #18c98f; color: #182138; }
        .strat-btn-ghost { background: #182138; color: #fff; }
        .strat-doc { max-width: 920px; margin: 0 auto; padding: 48px 32px 80px; line-height: 1.85; color: #0f172a; }

        .strat-cover { padding: 32px 0; border-bottom: 4px solid #18c98f; margin-bottom: 48px; }
        .strat-brand { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
        .strat-brand img { width: 56px; height: 46px; object-fit: contain; }
        .strat-brand-name { font-size: 22px; font-weight: 800; color: #182138; }
        .strat-brand-sub { font-size: 12px; color: #475569; letter-spacing: 2px; font-weight: 700; text-transform: uppercase; margin-top: 2px; }
        .strat-cover h1 { font-size: 32px; font-weight: 900; line-height: 1.3; color: #182138; margin: 0 0 16px; }
        .strat-lead { font-size: 16px; color: #334155; line-height: 1.9; margin: 0 0 20px; max-width: 800px; }
        .strat-meta { display: flex; gap: 24px; font-size: 12px; color: #64748b; flex-wrap: wrap; }
        .strat-meta span { padding: 4px 12px; border-radius: 999px; background: #f1f5f9; font-weight: 600; }

        .strat-sec { margin-bottom: 44px; }
        .strat-sec h2 { font-size: 24px; font-weight: 900; color: #182138; border-right: 6px solid #18c98f; padding: 4px 14px 4px 0; margin: 0 0 20px; }
        .strat-sec h3 { font-size: 16px; font-weight: 800; color: #12a777; margin: 24px 0 10px; }
        .strat-sec p { font-size: 14px; color: #1e293b; margin: 0 0 12px; }

        .strat-list { margin: 8px 0 16px; padding-right: 24px; }
        .strat-list li { margin-bottom: 8px; font-size: 14px; color: #0f172a; }
        .strat-list-ol { counter-reset: itm; list-style: none; padding-right: 0; }
        .strat-list-ol li { counter-increment: itm; position: relative; padding-right: 36px; margin-bottom: 12px; }
        .strat-list-ol li::before { content: counter(itm); position: absolute; right: 0; top: 2px; width: 26px; height: 26px; background: #18c98f; color: #182138; border-radius: 999px; font-weight: 800; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }

        .strat-table { width: 100%; border-collapse: collapse; font-size: 13px; margin: 12px 0 16px; }
        .strat-table th, .strat-table td { border: 1px solid #cbd5e1; padding: 10px 12px; text-align: right; vertical-align: top; }
        .strat-table th { background: #f1f5f9; font-weight: 800; color: #182138; }
        .strat-table-sm th, .strat-table-sm td { padding: 6px 8px; font-size: 11px; text-align: center; }
        .strat-total-row td { background: #dcfce7 !important; font-weight: 800; color: #065f46; }
        .strat-highlight td { background: #ecfdf5; font-weight: 700; }
        .strat-note { font-size: 11px; color: #64748b; font-style: italic; margin-top: 6px; }

        .strat-swot { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 12px 0; }
        .strat-swot-cell { padding: 16px; border-radius: 12px; border: 2px solid; }
        .strat-swot-cell h4 { font-size: 15px; font-weight: 900; margin: 0 0 10px; }
        .strat-swot-cell ul { padding-right: 20px; margin: 0; font-size: 13px; line-height: 1.8; }
        .strat-swot-cell ul li { margin-bottom: 4px; }
        .strat-s { background: #ecfdf5; border-color: #86efac; }
        .strat-s h4 { color: #065f46; }
        .strat-w { background: #fef3c7; border-color: #fcd34d; }
        .strat-w h4 { color: #92400e; }
        .strat-o { background: #dbeafe; border-color: #93c5fd; }
        .strat-o h4 { color: #1e40af; }
        .strat-t { background: #fee2e2; border-color: #fca5a5; }
        .strat-t h4 { color: #991b1b; }

        .strat-signoff { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 32px; padding: 20px; background: #f8fafc; border-radius: 12px; border: 1px solid #cbd5e1; }
        .strat-signoff-label { font-size: 11px; color: #64748b; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; }
        .strat-signoff-value { font-size: 14px; font-weight: 700; color: #182138; margin-top: 4px; }

        .strat-footer { margin-top: 60px; padding-top: 20px; border-top: 2px solid #18c98f; display: flex; justify-content: space-between; font-size: 11px; color: #64748b; }

        @page { size: A4; margin: 14mm 12mm; }
        @media print {
          .no-print { display: none !important; }
          .strat-doc { max-width: none; padding: 0; }
          .strat-sec { page-break-inside: avoid; }
          .strat-page-break { page-break-before: always; }
          .strat-swot { grid-template-columns: 1fr 1fr; }
          body { background: #fff !important; }
        }
        @media (max-width: 640px) {
          .strat-swot { grid-template-columns: 1fr; }
          .strat-signoff { grid-template-columns: 1fr; }
          .strat-doc { padding: 60px 16px 60px; }
          .strat-cover h1 { font-size: 22px; }
          .strat-table { font-size: 11px; }
          .strat-table th, .strat-table td { padding: 6px; }
        }
      `}</style>
    </div>
  );
}
